import { createContext, useContext, useState, useCallback, useRef } from "react";
import type { Agent } from "../lib/api";

export interface ChatMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  thinking?: string;
  timestamp: Date;
  streaming?: boolean;
  progress?: { label: string; percent: number; done: boolean }[];
}

export interface ChatSession {
  id: string;
  title: string;
  agentId?: string;
  agentName?: string;
  createdAt: Date;
  messages: ChatMessage[];
}

const MAX_SESSIONS = 20;

interface ChatContextValue {
  chatOpen: boolean;
  setChatOpen: (open: boolean) => void;
  /** Create a new session and send first message to it. Returns session ID. */
  startSession: (opts: { title: string; message: string; agentId?: string }) => string;
  /** Create a new session with a loading assistant message. Returns session ID. */
  startSessionLoading: (opts: { title: string }) => string;
  /** Append a new loading message to an existing session. Returns the message ID. */
  appendLoadingToSession: (sessionId: string) => string;
  /** Resolve a loading message with the final result (or error). */
  resolveSession: (sessionId: string, content: string, messageId?: string) => void;
  /** Find an existing session by title. */
  findSessionByTitle: (title: string) => ChatSession | undefined;
  /** Update messages for a specific session (immutable). */
  updateSessionMessages: (sessionId: string, updater: ChatMessage[] | ((prev: ChatMessage[]) => ChatMessage[])) => void;
  /** Send a follow-up message in current session */
  sendMessage: (text: string) => void;
  /** Session management */
  sessions: ChatSession[];
  activeSessionId: string | null;
  setActiveSessionId: (id: string | null) => void;
  deleteSession: (id: string) => void;
  /** Pending message (internal, for ChatPanel to consume) */
  pendingMessage: string | null;
  consumePendingMessage: () => string | null;
  /** All loaded agents (populated by ChatPanel) */
  agentList: Agent[];
  setAgentList: (agents: Agent[]) => void;
}

const ChatContext = createContext<ChatContextValue | null>(null);

export function ChatProvider({ children }: { children: React.ReactNode }) {
  const [chatOpen, setChatOpen] = useState(true);
  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);
  const [agentList, setAgentList] = useState<Agent[]>([]);
  const pendingRef = useRef<string | null>(null);
  const [, setTick] = useState(0);

  const startSession = useCallback((opts: { title: string; message: string; agentId?: string }): string => {
    const id = crypto.randomUUID();
    const agent = opts.agentId ? agentList.find((a) => a._id === opts.agentId) : undefined;
    const session: ChatSession = {
      id,
      title: opts.title,
      agentId: opts.agentId,
      agentName: agent?.name,
      createdAt: new Date(),
      messages: [],
    };

    setSessions((prev) => {
      const next = [session, ...prev];
      // Prune oldest if over limit
      if (next.length > MAX_SESSIONS) {
        return next.slice(0, MAX_SESSIONS);
      }
      return next;
    });
    setActiveSessionId(id);
    pendingRef.current = opts.message;
    setChatOpen(true);
    setTick((t) => t + 1);
    return id;
  }, [agentList]);

  const startSessionLoading = useCallback((opts: { title: string }): string => {
    const id = crypto.randomUUID();
    const session: ChatSession = {
      id,
      title: opts.title,
      createdAt: new Date(),
      messages: [
        {
          id: `loading-${id}`,
          role: "assistant",
          content: "",
          timestamp: new Date(),
          streaming: true,
        },
      ],
    };

    setSessions((prev) => {
      const next = [session, ...prev];
      if (next.length > MAX_SESSIONS) {
        return next.slice(0, MAX_SESSIONS);
      }
      return next;
    });
    setActiveSessionId(id);
    setChatOpen(true);
    setTick((t) => t + 1);
    return id;
  }, []);

  const appendLoadingToSession = useCallback((sessionId: string): string => {
    const msgId = `loading-${crypto.randomUUID()}`;
    setSessions((prev) =>
      prev.map((s) => {
        if (s.id !== sessionId) return s;
        return {
          ...s,
          messages: [
            ...s.messages,
            {
              id: msgId,
              role: "assistant" as const,
              content: "",
              timestamp: new Date(),
              streaming: true,
            },
          ],
        };
      })
    );
    setActiveSessionId(sessionId);
    setChatOpen(true);
    setTick((t) => t + 1);
    return msgId;
  }, []);

  const findSessionByTitle = useCallback((title: string): ChatSession | undefined => {
    return sessions.find((s) => s.title === title);
  }, [sessions]);

  const resolveSession = useCallback((sessionId: string, content: string, messageId?: string) => {
    setSessions((prev) =>
      prev.map((s) => {
        if (s.id !== sessionId) return s;
        const targetId = messageId ?? `loading-${sessionId}`;
        return {
          ...s,
          messages: s.messages.map((m) =>
            m.id === targetId
              ? { ...m, content, streaming: false }
              : m
          ),
        };
      })
    );
    setTick((t) => t + 1);
  }, []);

  const updateSessionMessages = useCallback((sessionId: string, updater: ChatMessage[] | ((prev: ChatMessage[]) => ChatMessage[])) => {
    setSessions((prev) =>
      prev.map((s) => {
        if (s.id !== sessionId) return s;
        const next = typeof updater === "function" ? updater(s.messages) : updater;
        return { ...s, messages: next };
      })
    );
  }, []);

  const sendMessage = useCallback((text: string) => {
    // If no active session, create a "General" session
    if (!activeSessionId) {
      const id = crypto.randomUUID();
      const session: ChatSession = {
        id,
        title: "General",
        createdAt: new Date(),
        messages: [],
      };
      setSessions((prev) => [session, ...prev]);
      setActiveSessionId(id);
    }
    pendingRef.current = text;
    setChatOpen(true);
    setTick((t) => t + 1);
  }, [activeSessionId]);

  const deleteSession = useCallback((id: string) => {
    setSessions((prev) => prev.filter((s) => s.id !== id));
    setActiveSessionId((current) => current === id ? null : current);
  }, []);

  const consumePendingMessage = useCallback(() => {
    const msg = pendingRef.current;
    pendingRef.current = null;
    return msg;
  }, []);

  return (
    <ChatContext.Provider
      value={{
        chatOpen,
        setChatOpen,
        startSession,
        startSessionLoading,
        appendLoadingToSession,
        resolveSession,
        findSessionByTitle,
        updateSessionMessages,
        sendMessage,
        sessions,
        activeSessionId,
        setActiveSessionId,
        deleteSession,
        pendingMessage: pendingRef.current,
        consumePendingMessage,
        agentList,
        setAgentList,
      }}
    >
      {children}
    </ChatContext.Provider>
  );
}

export function useChat(): ChatContextValue {
  const ctx = useContext(ChatContext);
  if (!ctx) throw new Error("useChat must be used within ChatProvider");
  return ctx;
}
